/**
 * Menu Builder
 * 将后端返回的扁平菜单数组转换为树形结构，并提供辅助查询函数
 *
 * Requirements: 4.2, 4.3, 4.4, 4.7, 4.8, 4.9
 */
import React from 'react';

// ============ 类型定义 ============

/** 后端菜单项（扁平结构） */
export interface BackendMenuItem {
  id: string;
  name: string;
  url: string;
  icon: string;
  level: number;
  serial_number: number;
  /** 1=叶子节点, 0=非叶子 */
  leaf: number;
  /** 空字符串=根节点 */
  parent_id: string;
  cache: boolean;
  component: string;
  part_name: string;
  external_link: boolean;
  type: 'group' | 'menu' | 'hidden';
}

/** 菜单树节点（前端使用） */
export interface MenuTreeNode {
  id: string;
  name: string;
  url: string;
  icon: string;
  level: number;
  serialNumber: number;
  isLeaf: boolean;
  parentId: string;
  externalLink: boolean;
  type: 'group' | 'menu' | 'hidden';
  children: MenuTreeNode[];
  /** 保留原始后端数据 */
  raw: BackendMenuItem;
}

/** ProLayout 菜单数据格式 */
export interface MenuDataItem {
  name?: string;
  icon?: React.ReactNode;
  path?: string;
  hideInMenu?: boolean;
  children?: MenuDataItem[];
  [key: string]: any;
}

import { getMenuIcon } from './icon-map';

// ============ 内部辅助函数 ============

/**
 * 将后端菜单项转换为菜单树节点（不含 children）
 */
function toTreeNode(item: BackendMenuItem): MenuTreeNode {
  return {
    id: item.id,
    name: item.name,
    url: item.url,
    icon: item.icon,
    level: item.level,
    serialNumber: item.serial_number,
    isLeaf: item.leaf === 1,
    parentId: item.parent_id,
    externalLink: item.external_link,
    type: item.type,
    children: [],
    raw: item,
  };
}

/**
 * 按 serial_number 升序排序比较函数
 */
function sortBySerialNumber(a: MenuTreeNode, b: MenuTreeNode): number {
  return a.serialNumber - b.serialNumber;
}

// ============ 核心函数 ============

/**
 * 将扁平菜单数组转换为树形结构
 *
 * - parent_id 为空字符串的项为根节点
 * - parent_id 无效（在数组中找不到对应 id）的项降级为根节点
 * - 同一层级按 serial_number 升序排列
 * - 每个节点的 raw 字段保留原始 BackendMenuItem 数据
 *
 * @param menus - 后端返回的扁平菜单数组
 * @returns 树形结构的菜单节点数组
 */
export function buildMenuTree(menus: BackendMenuItem[]): MenuTreeNode[] {
  if (!menus || menus.length === 0) {
    return [];
  }

  // 1. 构建 id → node 映射
  const nodeMap = new Map<string, MenuTreeNode>();
  for (const item of menus) {
    nodeMap.set(item.id, toTreeNode(item));
  }

  // 2. 构建树：将子节点挂到父节点的 children 中
  const roots: MenuTreeNode[] = [];

  for (const item of menus) {
    const node = nodeMap.get(item.id);
    if (!node) continue;
    const isRoot = item.parent_id === '';
    const parentNode =
      item.parent_id !== '' ? nodeMap.get(item.parent_id) : undefined;

    if (isRoot || !parentNode) {
      // 根节点 或 无效 parent_id → 降级为根节点
      roots.push(node);
    } else {
      parentNode.children.push(node);
    }
  }

  // 3. 递归排序每一层
  function sortChildren(nodes: MenuTreeNode[]): void {
    nodes.sort(sortBySerialNumber);
    for (const node of nodes) {
      if (node.children.length > 0) {
        sortChildren(node.children);
      }
    }
  }

  sortChildren(roots);

  return roots;
}

/**
 * 将菜单树转换为 ProLayout MenuDataItem 格式
 *
 * name 使用后端返回的原始名称。配合 ProLayout 的 locale: true，
 * ProLayout 会自动生成 locale key（格式为 "menu.{name}"），
 * 并调用 formatMessage({ id: "menu.{name}", defaultMessage: name }) 进行翻译。
 * 如果 locale 文件中没有对应的 key，则 fallback 显示原始名称。
 *
 * @param tree - 菜单树节点数组
 * @returns ProLayout 菜单数据数组
 */
export function toProLayoutMenuData(tree: MenuTreeNode[]): MenuDataItem[] {
  return tree.map((node) => {
    const item: MenuDataItem = {
      key: node.id,
      name: node.name,
      icon: getMenuIcon(node.icon),
      path: node.url,
    };

    if (node.type === 'hidden') {
      item.hideInMenu = true;
      item.hideChildrenInMenu = true;
    }

    if (node.externalLink) {
      item.externalLink = true;
    }

    if (node.children.length > 0) {
      item.children = toProLayoutMenuData(node.children);
    }

    return item;
  });
}

/**
 * 在菜单树中按 URL 查找节点（深度优先搜索）
 *
 * @param tree - 菜单树节点数组
 * @param url - 要查找的 URL
 * @returns 匹配的菜单节点，未找到返回 null
 */
export function findMenuByUrl(
  tree: MenuTreeNode[],
  url: string,
): MenuTreeNode | null {
  for (const node of tree) {
    if (node.url === url) {
      return node;
    }
    if (node.children.length > 0) {
      const found = findMenuByUrl(node.children, url);
      if (found) return found;
    }
  }
  return null;
}

/**
 * 在菜单树中按 ID 查找节点（深度优先搜索）
 *
 * @param tree - 菜单树节点数组
 * @param id - 要查找的菜单 ID
 * @returns 匹配的菜单节点，未找到返回 null
 */
export function findMenuById(
  tree: MenuTreeNode[],
  id: string,
): MenuTreeNode | null {
  for (const node of tree) {
    if (node.id === id) {
      return node;
    }
    if (node.children.length > 0) {
      const found = findMenuById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * 将菜单树扁平化为一维数组
 *
 * @param tree - 菜单树节点数组
 * @returns 扁平化后的菜单节点数组
 */
export function flattenMenuTree(tree: MenuTreeNode[]): MenuTreeNode[] {
  const result: MenuTreeNode[] = [];

  function traverse(nodes: MenuTreeNode[]): void {
    for (const node of nodes) {
      result.push(node);
      if (node.children.length > 0) {
        traverse(node.children);
      }
    }
  }

  traverse(tree);
  return result;
}

/**
 * 获取从根节点到目标节点的路径
 *
 * @param tree - 菜单树节点数组
 * @param id - 目标菜单节点 ID
 * @returns 从根到目标节点的路径数组，未找到返回空数组
 */
export function getMenuPath(tree: MenuTreeNode[], id: string): MenuTreeNode[] {
  function findPath(
    nodes: MenuTreeNode[],
    targetId: string,
    path: MenuTreeNode[],
  ): MenuTreeNode[] | null {
    for (const node of nodes) {
      const currentPath = [...path, node];
      if (node.id === targetId) {
        return currentPath;
      }
      if (node.children.length > 0) {
        const found = findPath(node.children, targetId, currentPath);
        if (found) return found;
      }
    }
    return null;
  }

  return findPath(tree, id, []) ?? [];
}
